import * as Toast from '@radix-ui/react-toast'
import { keyframes, styled } from '../../../config'

const Provider = Toast.Provider

const VIEWPORT_PADDING = 25

const Viewport = styled(Toast.Viewport, {
  position: 'fixed',
  bottom: 0,
  right: 0,
  display: 'flex',
  flexDirection: 'column',
  padding: VIEWPORT_PADDING,
  gap: 10,
  width: 390,
  maxWidth: '100vw',
  margin: 0,
  listStyle: 'none',
  zIndex: 2147483647,
  outline: 'none'
})

const hide = keyframes({
  '0%': { opacity: 1 },
  '100%': { opacity: 0 }
})

const slideIn = keyframes({
  from: { transform: `translateX(calc(100% + ${VIEWPORT_PADDING}px))` },
  to: { transform: 'translateX(0)' }
})

const swipeOut = keyframes({
  from: { transform: 'translateX(var(--radix-toast-swipe-end-x))' },
  to: { transform: `translateX(calc(100% + ${VIEWPORT_PADDING}px))` }
})

const Root = styled(Toast.Root, {
  background: 'rgba(36, 36, 36, 0.8)',
  borderStyle: 'solid',
  borderColor: '$whiteA100',
  borderWidth: '1px',
  boxSizing: 'border-box',
  borderRadius: '8px',
  padding: '8px 12px',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  alignItems: 'start',
  gap: '4px',
  transition: 'transform 200ms ease-out',
  '&[data-state="open"]': {
    animation: `${slideIn} 150ms cubic-bezier(0.16, 1, 0.3, 1)`
  },
  '&[data-state="closed"]': {
    animation: `${hide} 100ms ease-in`
  },
  '&[data-swipe="move"]': {
    transform: 'translateX(var(--radix-toast-swipe-move-x))'
  },
  '&[data-swipe="cancel"]': {
    transform: 'translateX(0)',
    transition: 'transform 200ms ease-out'
  },
  '&[data-swipe="end"]': {
    animation: `${swipeOut} 100ms ease-out`
  },
  variants: {
    variant: {
      success: { borderColor: '$green0600', background: '$green1000' },
      info: { borderColor: '$whiteA100' },
      error: { borderColor: '$red0600', background: '$red1000' }
    }
  }
})

const Title = styled(Toast.Title, {
  gridArea: 'title',
  fontWeight: 500,
  color: '$white',
  text: '16'
})

const Description = styled(Toast.Description, {
  gridArea: 'description',
  margin: 0,
  color: '$whiteA600',
  text: '12'
})

const Action = styled(Toast.Action, {
  gridArea: 'action',
  outline: 'none',
  border: 'none',
  margin: '-2px -6px',
  marginTop: '0',
  padding: '2px 6px',
  text: '12',
  borderRadius: '4px',
  fontWeight: 500,
  background: 'transparent',
  transition: 'all 200ms ease',
  '&:hover': { background: '$whiteA050', color: '$white' },
  variants: {
    variant: {
      success: { color: '$green0300' },
      info: { color: '$white' },
      error: { color: '$red0300' }
    }
  }
})

const Close = styled(Toast.Close, {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',
  outline: 'none',
  background: 'transparent',
  color: '$whiteA500',
  position: 'absolute',
  borderRadius: '4px',
  padding: '4px',
  top: '4px',
  right: '4px',
  transition: 'all 100ms ease',
  '& > svg': {
    width: '16px',
    height: '16px'
  },
  '&:hover': {
    color: '$white',
    background: '$whiteA050'
  }
})

export const ToastPrimitive = { Provider, Viewport, Root, Title, Description, Action, Close }
